import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, withCtx as _withCtx, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "mb-10" }
const _hoisted_2 = { key: 0 }
const _hoisted_3 = { key: 1 }
const _hoisted_4 = ["href"]
const _hoisted_5 = { class: "sr-only" }
const _hoisted_6 = { key: 2 }
const _hoisted_7 = { class: "mt-20" }
const _hoisted_8 = {
  key: 0,
  class: "row"
}
const _hoisted_9 = {
  key: 0,
  class: "mb-10"
}
const _hoisted_10 = {
  key: 0,
  class: "mb-10"
}

import { _CREATE, _VIEW, _EDIT } from '@shell/config/query-params';
import { ref, computed, useTemplateRef, watch } from 'vue';
import { useStore } from 'vuex';
import semver from 'semver';
import { useI18n } from '@shell/composables/useI18n';
import Checkbox from '@components/Form/Checkbox/Checkbox.vue';
import { Banner } from '@components/Banner';
import IngressCards from '@shell/edit/provisioning.cattle.io.cluster/ingress/IngressCards.vue';
import {
  INGRESS_OPTIONS, INGRESS_DUAL, TRAEFIK, INGRESS_NGINX, INGRESS_NONE, INGRESS_MIGRATION_KB_LINK, INGRESS_CLASS_DEFAULT, INGRESS_CONTROLLER_CLASS_DEFAULT, INGRESS_CLASS_MIGRATION, INGRESS_CONTROLLER_CLASS_MIGRATION
} from '@shell/edit/provisioning.cattle.io.cluster/shared';
import IngressConfiguration from '@shell/edit/provisioning.cattle.io.cluster/ingress/IngressConfiguration.vue';
import YamlEditor, { EDITOR_MODES } from '@shell/components/YamlEditor';
import { set, get, mergeWithReplace } from '@shell/utils/object';
import { saferDump } from '@shell/utils/create-yaml';
import RichTranslation from '@shell/components/RichTranslation.vue';

interface Props {
  mode?: string;
  value: string | string[];
  nginxSupported: boolean;
  traefikSupported: boolean;
  nginxChart: string;
  traefikChart: string;
  userChartValues: any;
  versionInfo: any;
  originalIngressController?: string | string[];
}

export default /*@__PURE__*/_defineComponent({
  __name: 'Ingress',
  props: {
    mode: { default: () => (_CREATE) },
    value: {},
    nginxSupported: { type: Boolean },
    traefikSupported: { type: Boolean },
    nginxChart: {},
    traefikChart: {},
    userChartValues: {},
    versionInfo: {},
    originalIngressController: { default: () => (INGRESS_NONE) }
  },
  emits: ['update:value', 'error', 'config-validation-changed', 'yaml-validation-changed', 'update-values'],
  setup(__props: any, { emit: __emit }) {



const emit = __emit;
const store = useStore();
const { t } = useI18n(store);
const nginxYaml = useTemplateRef('nginx-yaml');
const traefikYaml = useTemplateRef('traefik-yaml');

const showAdvanced = ref<Boolean>(false);
const isView = computed(() => __props.mode === _VIEW);
const isEdit = computed(() => __props.mode === _EDIT);
const showTraefikBanner = ref<Boolean>(false);
const traefikMerged = ref(initYamlEditor(__props.traefikChart));
const nginxMerged = ref(initYamlEditor(__props.nginxChart));
const showConfig = computed(() => !!__props.versionInfo[__props.traefikChart] || !!__props.versionInfo[__props.nginxChart]);

// in traefik v40 the nginx key changed from kubernetesIngressNginx to kubernetesIngressNGINX
const traefikNginxKey = computed(() => {
  const chartVersion = __props.versionInfo[__props.traefikChart]?.chart?.version;

  // regex: replace zeros that follow . and precede digits with just the .<digit> eg 32.1.002 -> 32.1.2
  // semver coerce will return null if version has leading zeros
  const cleaned = chartVersion?.replace(/\.0+(\d)/g, '.$1');
  const coerced = cleaned ? semver.coerce(cleaned) : null;

  if (coerced && semver.lt(coerced, '40.0.0')) {
    return 'kubernetesIngressNginx';
  }

  return 'kubernetesIngressNGINX';
});

watch(traefikNginxKey, (newKey, oldKey) => {
  if (oldKey && newKey !== oldKey) {
    const oldValues = get(traefikMerged.value, `providers.${ oldKey }`);

    if (oldValues) {
      set(traefikMerged.value, `providers.${ newKey }`, oldValues);
      delete (traefikMerged.value as any)?.providers?.[oldKey];

      // Only emit if the user had previously saved values under the old key
      // this will cover the scenario where a user is using 'dual mode' then upgrades k8s version to one that uses a newer traefik version
      const savedOldValues = get(__props.userChartValues[__props.traefikChart], `providers.${ oldKey }`);

      if (savedOldValues) {
        emit('update-values', __props.traefikChart, traefikMerged.value);
        updateYaml(traefikYaml.value, traefikMerged.value);
      }
    }
  }
});

const ingressSelection = computed(() => {
  if (Array.isArray(__props.value) ) {
    return INGRESS_DUAL;
  } else if (!__props.value || __props.value.length === 0) {
    return INGRESS_NONE;
  } else {
    return __props.value;
  }
});
const ingressOptions = computed(() => {
  return INGRESS_OPTIONS.filter((option) => {
    if (option.id === INGRESS_NGINX) {
      return __props.nginxSupported;
    } else if (option.id === TRAEFIK) {
      return __props.traefikSupported;
    } else if (option.id === INGRESS_DUAL) {
      if (__props.mode === _CREATE) {
        return false;
      } else {
        return __props.traefikSupported && __props.nginxSupported;
      }
    }

    return true;
  }).map((option) => {
    return {
      ...option,
      selected: option.id === ingressSelection.value
    };
  });
});

const ingressEnabled = computed({
  get() {
    return ingressSelection.value !== INGRESS_NONE;
  },
  set(val) {
    if (!val) {
      emit('update:value', INGRESS_NONE);
    } else {
      if (__props.traefikSupported) {
        emit('update:value', TRAEFIK);
      } else if (__props.nginxSupported) {
        emit('update:value', INGRESS_NGINX);
      } else {
        emit('update:value', INGRESS_NONE);
      }
    }
  }
});

function initYamlEditor(chart: string) {
  const defaultChartValue = __props.versionInfo[chart];

  return mergeWithReplace(defaultChartValue?.values, __props.userChartValues[chart]);
}

function setCompatibilityModeValues(val: boolean) {
  const key = traefikNginxKey.value;

  set(traefikMerged.value, `providers.${ key }.enabled`, val);
  if (!val) {
    set(traefikMerged.value, `providers.${ key }.ingressClass`, INGRESS_CLASS_DEFAULT);
    set(traefikMerged.value, `providers.${ key }.controllerClass`, INGRESS_CONTROLLER_CLASS_DEFAULT);
  } else {
    set(traefikMerged.value, `providers.${ key }.ingressClass`, INGRESS_CLASS_MIGRATION);
    set(traefikMerged.value, `providers.${ key }.controllerClass`, INGRESS_CONTROLLER_CLASS_MIGRATION);
  }
}

function preconfigureTraefik() {
  set(traefikMerged.value, 'ports.web.hostPort', 8000);
  set(traefikMerged.value, 'ports.websecure.hostPort', 8443);
  setCompatibilityModeValues(true);
  emit('update-values', __props.traefikChart, traefikMerged.value);
}

const nginxHttp = computed({
  get() {
    return get(nginxMerged.value, 'controller.hostPort.ports.http');
  },
  set(val: string) {
    set(nginxMerged.value, 'controller.hostPort.ports.http', Number(val));
    emit('update-values', __props.nginxChart, nginxMerged.value);
    updateYaml(nginxYaml.value, nginxMerged.value);
  }
});
const nginxHttps = computed({
  get() {
    return get(nginxMerged.value, 'controller.hostPort.ports.https');
  },
  set(val: string) {
    set(nginxMerged.value, 'controller.hostPort.ports.https', Number(val));
    emit('update-values', __props.nginxChart, nginxMerged.value);
    updateYaml(nginxYaml.value, nginxMerged.value);
  }
});
const traefikHttp = computed({
  get() {
    return get(traefikMerged.value, 'ports.web.hostPort');
  },
  set(val: string) {
    set(traefikMerged.value, 'ports.web.hostPort', Number(val));
    emit('update-values', __props.traefikChart, traefikMerged.value);
    updateYaml(traefikYaml.value, traefikMerged.value);
  }
});
const traefikHttps = computed({
  get() {
    return get(traefikMerged.value, 'ports.websecure.hostPort');
  },
  set(val: string) {
    set(traefikMerged.value, 'ports.websecure.hostPort', Number(val));
    emit('update-values', __props.traefikChart, traefikMerged.value);
    updateYaml(traefikYaml.value, traefikMerged.value);
  }
});

const compatibilityMode = computed({
  get() {
    return get(traefikMerged.value, `providers.${ traefikNginxKey.value }.enabled`);
  },
  set(val: boolean) {
    setCompatibilityModeValues(val);
    emit('update-values', __props.traefikChart, traefikMerged.value);
    updateYaml(traefikYaml.value, traefikMerged.value);
  }
});

function selectIngress(id: string) {
  if ( id === INGRESS_DUAL) {
    const newValue: string | string[] = !Array.isArray(__props.originalIngressController) ? (__props.originalIngressController === TRAEFIK ? [TRAEFIK, INGRESS_NGINX] : [INGRESS_NGINX, TRAEFIK]) : __props.originalIngressController;

    emit('update:value', newValue);
    preconfigureTraefik();
  } else {
    emit('update:value', id);
    if (id === TRAEFIK && !!isEdit.value) {
      showTraefikBanner.value = true;
    }
  }
}

function updateYaml(component: any, value: any) {
  if (component) {
    component.updateValue(saferDump(value));
  }
}


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("h3", _hoisted_1, _toDisplayString(_unref(t)('cluster.ingress.title')), 1),
    _cache[22] || (_cache[22] = _createTextVNode()),
    _createVNode(Checkbox, {
      value: ingressEnabled.value,
      "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((ingressEnabled).value = $event)),
      mode: _ctx.mode,
      label: _unref(t)('cluster.ingress.enableIngress')
    }, null, 8, ["value", "mode", "label"]),
    _cache[23] || (_cache[23] = _createTextVNode()),
    (!ingressEnabled.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(_unref(Banner), {
            color: "warning",
            "label-key": "cluster.ingress.banners.disabled.label"
          })
        ]))
      : (_openBlock(), _createElementBlock("div", _hoisted_3, [
          (_ctx.traefikSupported)
            ? (_openBlock(), _createBlock(_unref(Banner), {
                key: 0,
                color: "info",
                "label-key": "cluster.ingress.banners.transitioning.label"
              }))
            : _createCommentVNode("", true),
          _cache[19] || (_cache[19] = _createTextVNode()),
          _createVNode(IngressCards, {
            options: ingressOptions.value,
            mode: _ctx.mode,
            class: _normalizeClass(!_ctx.traefikSupported ? 'mt-10' : ''),
            onSelect: selectIngress
          }, null, 8, ["options", "mode", "class"]),
          _cache[20] || (_cache[20] = _createTextVNode()),
          ((isEdit.value && ingressSelection.value !== _unref(TRAEFIK)) || showTraefikBanner.value)
            ? (_openBlock(), _createBlock(_unref(Banner), {
                key: 1,
                color: "warning"
              }, {
                default: _withCtx(() => [
                  _createVNode(RichTranslation, {
                    k: `cluster.ingress.banners.selected.${ingressSelection.value}.label`
                  }, {
                    docsUrl: _withCtx(({ content }) => [
                      _createElementVNode("a", {
                        href: `${_unref(INGRESS_MIGRATION_KB_LINK)}`,
                        tabindex: "0",
                        target: "_blank",
                        rel: "noopener noreferrer nofollow"
                      }, [
                        _createTextVNode(_toDisplayString(content) + " ", 1),
                        _cache[12] || (_cache[12] = _createElementVNode("i", { class: "icon icon-external-link" }, null, -1)),
                        _cache[13] || (_cache[13] = _createTextVNode()),
                        _createElementVNode("span", _hoisted_5, _toDisplayString(_unref(t)('generic.opensInNewTab')), 1)
                      ], 8, _hoisted_4)
                    ]),
                    _: 1
                  }, 8, ["k"])
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          _cache[21] || (_cache[21] = _createTextVNode()),
          (showConfig.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                _createElementVNode("div", _hoisted_7, [
                  _createVNode(IngressConfiguration, {
                    "compatibility-mode": compatibilityMode.value,
                    "onUpdate:compatibilityMode": _cache[1] || (_cache[1] = ($event: any) => ((compatibilityMode).value = $event)),
                    nginxHttp: nginxHttp.value,
                    "onUpdate:nginxHttp": _cache[2] || (_cache[2] = ($event: any) => ((nginxHttp).value = $event)),
                    nginxHttps: nginxHttps.value,
                    "onUpdate:nginxHttps": _cache[3] || (_cache[3] = ($event: any) => ((nginxHttps).value = $event)),
                    traefikHttp: traefikHttp.value,
                    "onUpdate:traefikHttp": _cache[4] || (_cache[4] = ($event: any) => ((traefikHttp).value = $event)),
                    traefikHttps: traefikHttps.value,
                    "onUpdate:traefikHttps": _cache[5] || (_cache[5] = ($event: any) => ((traefikHttps).value = $event)),
                    mode: _ctx.mode,
                    "ingress-selection": ingressSelection.value,
                    onValidationChanged: _cache[6] || (_cache[6] = ($event: any) => (emit('config-validation-changed', $event)))
                  }, null, 8, ["compatibility-mode", "nginxHttp", "nginxHttps", "traefikHttp", "traefikHttps", "mode", "ingress-selection"])
                ]),
                _cache[17] || (_cache[17] = _createTextVNode()),
                _createElementVNode("div", null, [
                  _createElementVNode("button", {
                    type: "button",
                    class: "btn role-link advanced-toggle mb-0",
                    onClick: _cache[7] || (_cache[7] = ($event: any) => (showAdvanced.value = !showAdvanced.value))
                  }, _toDisplayString(showAdvanced.value ? _unref(t)('cluster.ingress.hideAdvanced') : _unref(t)('cluster.ingress.showAdvanced')), 1)
                ]),
                _cache[18] || (_cache[18] = _createTextVNode()),
                (showAdvanced.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                      (ingressSelection.value === _unref(TRAEFIK) || ingressSelection.value === _unref(INGRESS_DUAL))
                        ? (_openBlock(), _createElementBlock("div", {
                            key: 0,
                            class: _normalizeClass({ 'col': true, 'span-6': ingressSelection.value === _unref(INGRESS_DUAL), 'span-12': ingressSelection.value !== _unref(INGRESS_DUAL) })
                          }, [
                            (ingressSelection.value === _unref(INGRESS_DUAL))
                              ? (_openBlock(), _createElementBlock("p", _hoisted_9, _toDisplayString(_unref(t)('cluster.ingress.traefik.header')), 1))
                              : _createCommentVNode("", true),
                            _cache[14] || (_cache[14] = _createTextVNode()),
                            _createVNode(_unref(YamlEditor), {
                              ref: "traefik-yaml",
                              class: "ingress-yaml-editor",
                              "data-testid": "traefik-yaml-editor",
                              value: traefikMerged.value,
                              mode: _ctx.mode,
                              scrolling: true,
                              "as-object": true,
                              "editor-mode": isView.value ? _unref(EDITOR_MODES).VIEW_CODE : _unref(EDITOR_MODES).EDIT_CODE,
                              "hide-preview-buttons": true,
                              "onUpdate:value": _cache[8] || (_cache[8] = ($event: any) => (emit('update-values', _ctx.traefikChart, $event))),
                              onValidationChanged: _cache[9] || (_cache[9] = ($event: any) => (emit('yaml-validation-changed', {name: _ctx.traefikChart, val: $event})))
                            }, null, 8, ["value", "mode", "editor-mode"])
                          ], 2))
                        : _createCommentVNode("", true),
                      _cache[16] || (_cache[16] = _createTextVNode()),
                      (ingressSelection.value === _unref(INGRESS_NGINX) || ingressSelection.value === _unref(INGRESS_DUAL))
                        ? (_openBlock(), _createElementBlock("div", {
                            key: 1,
                            class: _normalizeClass({ 'col': true, 'span-6': ingressSelection.value === _unref(INGRESS_DUAL), 'span-12': ingressSelection.value !== _unref(INGRESS_DUAL) })
                          }, [
                            (ingressSelection.value === _unref(INGRESS_DUAL))
                              ? (_openBlock(), _createElementBlock("p", _hoisted_10, _toDisplayString(_unref(t)('cluster.ingress.nginx.header')), 1))
                              : _createCommentVNode("", true),
                            _cache[15] || (_cache[15] = _createTextVNode()),
                            _createVNode(_unref(YamlEditor), {
                              ref: "nginx-yaml",
                              class: "ingress-yaml-editor",
                              "data-testid": "ingress-nginx-yaml-editor",
                              value: nginxMerged.value,
                              mode: _ctx.mode,
                              scrolling: true,
                              "as-object": true,
                              "editor-mode": isView.value ? _unref(EDITOR_MODES).VIEW_CODE : _unref(EDITOR_MODES).EDIT_CODE,
                              "hide-preview-buttons": true,
                              "onUpdate:value": _cache[10] || (_cache[10] = ($event: any) => (emit('update-values', _ctx.nginxChart, $event))),
                              onValidationChanged: _cache[11] || (_cache[11] = ($event: any) => (emit('yaml-validation-changed', {name: _ctx.nginxChart, val: $event})))
                            }, null, 8, ["value", "mode", "editor-mode"])
                          ], 2))
                        : _createCommentVNode("", true)
                    ]))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true)
        ]))
  ], 64))
}
}

})